import React from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { ipSharesSubnet } from '../utils/eventUtils';

/**
 * Right-side drawer listing the user's baseline sign-in IPs, split into clearly
 * separated 7-day and 30-day sections. Opened by clicking the "N known IPs" count
 * on the Baseline Comparison "IP in baseline" row (replaces the old hover tooltip,
 * which was hard to read). Each IP is classified against the alert's event IP:
 * exact match (green) or same-subnet /24-v4 //64-v6 neighbor (amber).
 *
 * Portaled to document.body so the alert table's overflow / stacking context can't
 * clip it (same reason the Alerts status dropdown portals).
 *
 * Props:
 *   onClose  — close handler (backdrop / X)
 *   eventIp  — the alert's IP, for highlighting (may be null)
 *   windows  — [{ label: '7-day', ips: string[] }, { label: '30-day', ips: string[] }]
 */
export default function BaselineIpListDrawer({ onClose, eventIp, windows = [] }) {
  const evLower = eventIp ? String(eventIp).trim().toLowerCase() : null;

  function classify(ip) {
    if (!evLower) return null;
    if (String(ip).trim().toLowerCase() === evLower) return 'exact';
    if (ipSharesSubnet(eventIp, ip)) return 'subnet';
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Drawer */}
      <div className="relative ml-auto w-full max-w-md bg-gray-900 border-l border-gray-800 shadow-2xl flex flex-col h-full">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <GlobeAltIcon className="h-5 w-5 text-blue-400" />
            <h2 className="text-base font-semibold text-gray-100">Baseline sign-in IPs</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm">
          {eventIp && (
            <div className="mb-3 text-xs">
              <span className="text-gray-500">Alert IP:&nbsp;</span>
              <span className="font-mono text-gray-100">{eventIp}</span>
            </div>
          )}
          {/* Legend */}
          <div className="flex items-center gap-4 mb-4 text-[11px]">
            <span className="text-green-400">● exact match</span>
            <span className="text-amber-400">● same subnet</span>
            <span className="text-gray-500">● other</span>
          </div>

          {windows.map((w, wi) => (
            <section key={w.label} className={wi > 0 ? 'mt-5 pt-4 border-t border-gray-800' : ''}>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-200">{w.label} baseline</h3>
                <span className="text-[11px] text-gray-500">
                  {w.ips.length} {w.ips.length === 1 ? 'IP' : 'IPs'}
                </span>
              </div>
              {w.ips.length === 0 ? (
                <p className="text-gray-600 italic text-xs">No baseline IPs in this window.</p>
              ) : (
                <ul className="rounded-lg border border-gray-800 divide-y divide-gray-800/60 overflow-hidden">
                  {w.ips.map((ip, i) => {
                    const c = classify(ip);
                    const rowCls = c === 'exact'
                      ? 'bg-green-500/10 text-green-300'
                      : c === 'subnet'
                        ? 'bg-amber-500/10 text-amber-300'
                        : 'text-gray-300';
                    return (
                      <li key={`${w.label}-${ip}-${i}`}
                          className={`flex items-center justify-between px-3 py-1.5 font-mono text-xs ${rowCls}`}>
                        <span className="truncate">{ip}</span>
                        {c === 'exact' && <span className="text-[10px] font-sans text-green-400 ml-2 shrink-0">exact match</span>}
                        {c === 'subnet' && <span className="text-[10px] font-sans text-amber-400 ml-2 shrink-0">same subnet</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
